import moment from 'moment';
import Image from 'next/image';
import React, { useCallback, useState } from 'react';
import { useCookies } from 'react-cookie';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { deleteServer, serverAction } from '../../../api/server';
import { COOKIE } from '../../../constants';
import { TidalButton } from '../../common/button';
import { TidalTag } from '../../common/tag';
import { instanceTypes } from './instaceData';

const TidalDashboarServerCardWrapper = styled.div`
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0px 10px 49px rgb(0 27 31 / 8%);
  padding: 23px 25px;
  width: 100%;
  border: 1px solid #e6e6e6;
  padding: 25px 23px;
  @media only screen and (max-width: 1100px) {
    box-shadow: 0 0 0;
  }
  @media only screen and (max-width: 370px) {
    padding: 16px;
  }
`;

const TidalDashboardServerCardHeader = styled.div`
  display: grid;
  grid-template-columns: 85px 1fr 60px;
  grid-gap: 0 8px;
  .tidal-server-image {
    border-radius: 10px;
  }
  .tidal-dashboard-server-header-details {
    display: flex;
    position: relative;
    overflow: hidden;
    justify-content: space-between;
    @media only screen and (max-width: 370px) {
      display: block;
    }

    & div {
      overflow: hidden;
    }

    .tidal-server-tooltip {
      opacity: 0;
      position: absolute;
      width: 100%;
      z-index: 10;
      top: 0px;
      cursor: pointer;
      transition: 0.3s;
      max-height: 80px;
      overflow: auto;
      .tidal-server-text-container {
        margin-top: 20px;
        margin-left: 2px;
        background: #e3e7f2;
        display: flex;
        align-items: center;
        padding: 4px 12px;
        border-radius: 8px;
      }
    }

    .tidal-server-tooltip:hover {
      opacity: 1;
      border-radius: 8px;
    }

    .name {
      position: relative;
      font-weight: 700;
      font-size: 18px;
      line-height: 22px;
      color: ${(props) => props.theme.colors.text[2]};
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }

    .region {
      margin-top: 5px;
      font-weight: 600;
      font-size: 12px;
      color: ${(props) => props.theme.colors.text[0]};
    }
  }
`;

const TidalDashboardServerCardBody = styled.div`
  border-top: 1px solid ${(props) => props.theme.colors.borderColors[1]};
  border-bottom: 1px solid ${(props) => props.theme.colors.borderColors[1]};
  margin: 20px 0;
  padding: 10px 0;
  & > .tidal-dashboard-server-body-row {
    display: flex;
    justify-content: space-between;
    & > .label {
      font-weight: 600;
      font-size: 14px;
      line-height: 17px;
      color: ${(props) => props.theme.colors.text[0]};
      text-align: left;
    }
    & > .value {
      font-weight: 600;
      font-size: 14px;
      line-height: 17px;
      color: ${(props) => props.theme.colors.text[2]};
      text-align: right;
    }
  }
  & > .tidal-dashboard-server-body-row:nth-child(2) {
    margin-top: 12px;
  }
`;

const TidalDashboardServerCardFooter = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 12px;
`;

const TidalDashboardServerCardMetaInfo = styled.div`
  display: flex;
  align-items: center;
  grid-gap: 8px;
  margin-top: 8px;
  & > div {
    background-color: ${(props) => props.theme.colors.tags[5]};
    height: 28px;
    width: 28px;
    border-radius: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

interface ITidalDashboardServerCard {
  name: string;
  image: string;
  region: string;
  os: string;
  provider: string;
  status: string;
  serverType: string;
  id: number;
  ipAddress: string;
  instanceId: string;
  updatedAt: string;
  onUpdate: () => void;
}

const TidalDashboardServerCard = ({
  name,
  image,
  region,
  os,
  provider,
  status,
  serverType,
  id,
  ipAddress,
  instanceId,
  updatedAt,
  onUpdate,
}: ITidalDashboardServerCard): JSX.Element => {
  const [isSaving, setIsSaving] = useState(false);
  const [isSnapshotting, setIsSnapshotting] = useState(false);
  const [isDeleting, SetIsDeleting] = useState(false);

  const [cookies, _, removeCookie] = useCookies([COOKIE.ACCESS_TOKEN]);

  const getEstimatedCost = () => {
    const instanceTypeData = instanceTypes[provider].find(
      (instanceType) => instanceType.type == serverType,
    );
    if (instanceTypeData) {
      return instanceTypeData?.price || 0;
    }
  };

  const getHours = useCallback(() => {
    const updateDate = new Date(updatedAt);
    const todayDate = new Date();
    const hourDiff = moment(todayDate).diff(moment(updateDate), 'hour');
    if (hourDiff === 0) {
      return '<1';
    } else {
      return hourDiff;
    }
  }, [updatedAt]);

  const handlePerformAction = async () => {
    setIsSaving(true);
    try {
      await serverAction({
        serverId: id,
        action: status == 'active' ? 'stop' : 'start',
      });
      onUpdate();
    } catch (e) {}
    setIsSaving(false);
  };

  const handleSnapshot = async () => {
    setIsSnapshotting(true);
    try {
      await serverAction({
        serverId: id,
        action: 'snapshot',
      });
      toast.success(
        'Snapshot started, depending on size this may take a while.',
      );
      onUpdate();
    } catch (e) {
      console.error(e);
      toast.error('Snapshot failed to create.');
    }
    setIsSnapshotting(false);
  };

  const handleDestroy = async () => {
    SetIsDeleting(true);
    try {
      await deleteServer({ serverId: id });
      onUpdate();
    } catch (e) {}
    SetIsDeleting(false);
  };

  return (
    <TidalDashboarServerCardWrapper>
      <TidalDashboardServerCardHeader>
        <img
          alt=""
          className="tidal-server-image"
          src={'/images/new/tidal.png'}
          width={80}
          height={80}
        />
        <div className="tidal-dashboard-server-header-details">
          <div>
            <p className="name">{name}</p>
            <div className="tidal-server-tooltip">
              <div className="tidal-server-text-container">{name}</div>
            </div>
            <p className="region">{region}</p>
            <TidalDashboardServerCardMetaInfo>
              <div>
                <img alt="" src="/images/new/windows.svg" />
              </div>
              <div>
                <img
                  alt=""
                  src={
                    provider == 'aws'
                      ? '/images/new/aws.svg'
                      : '/images/new/gcp.svg'
                  }
                />
              </div>
            </TidalDashboardServerCardMetaInfo>
          </div>
        </div>
        <div className="tidal-dashboard-server-header-status">
          {status == 'active' ? (
            <TidalTag type="success" title="Active" />
          ) : (
            <TidalTag type="error" title="Inactive" />
          )}
        </div>
      </TidalDashboardServerCardHeader>
      <TidalDashboardServerCardBody>
        <div className="tidal-dashboard-server-body-row">
          <p className="label">
            {status == 'active' ? 'Active for' : 'Stopped for'}
          </p>
          <p className="value">{getHours()} hours</p>
        </div>
        <div className="tidal-dashboard-server-body-row">
          <p className="label">Estimated cost</p>
          <p className="value">${getEstimatedCost()}/hour</p>
        </div>
        <div className="pt-2">
          <TidalButton
            type={'info'}
            text={'Snapshot'}
            tint={true}
            isLoading={isSnapshotting}
            onClick={handleSnapshot}
          />
        </div>
      </TidalDashboardServerCardBody>
      <TidalDashboardServerCardFooter>
        <TidalButton
          type={status == 'active' ? 'error' : 'success'}
          text={status == 'active' ? 'Stop' : 'Start'}
          tint={true}
          isLoading={isSaving}
          onClick={handlePerformAction}
        />
        <TidalButton
          type="error"
          text={'Destroy'}
          isLoading={isDeleting}
          tint={false}
          onClick={handleDestroy}
        />
      </TidalDashboardServerCardFooter>
    </TidalDashboarServerCardWrapper>
  );
};

export default TidalDashboardServerCard;
